import React, { useMemo, useState, useEffect, useCallback, useDeferredValue } from "react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { getDriverAttendanceRequest, approveAttendanceRequest, approveLogoutRequest, leaveApproveRequest, revertLogoutRequest, revertLoginRequest, getLogoutDetailsRequest, holdDutyRequest, releaseHoldCabRequest, resumeDutyCabRequest, editDriverLocationRequest, makeCabAvailableRequest } from "../redux/actions/driverAttendenceAction";
import { getLocationsRequest } from "../redux/actions/bookingActions";
import { useBranchId } from "../utils/hooks/useBranch";
import { useMountEffectOnce } from "../utils/hooks/useMountEffectOnce";
import {
  Button,
  Card,
  Input,
  Row,
  Col,
  Table,
  Typography,
  Space,
  Tag,
  Dropdown,
  Modal,
  Form,
  TimePicker,
  DatePicker,
  Select,
  message,
  Tooltip,
  Popconfirm,
  Divider,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { CSSProperties } from "react";
import {
  CarTaxiFront,
  Calendar,
  User,
  Phone,
  Car,
  Search,
  LogIn,
  LogOut,
  Clock,
  MapPin,
  Wifi,
  WifiOff,
  Pencil,
  ArrowDown,
} from "lucide-react";
import { socket, initSocket } from "../utils/socket";
import { InfoCircleOutlined, EllipsisOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

const PRIMARY = "#86398f";
const BG_PAGE = "#f8f5fa";
const TEXT_SECONDARY = "#6b5b72";

export interface DriverRow {
  key: string;
  cabId: string;
  vehicle: string;
  driverName: string;
  mobile: string;
  date: string;
  time: string;
  location: string;
  active: boolean;
  driverStatus:
  | "Waiting Login Approval"
  | "On Trip"
  | "Logout"
  | "Available"
  | "Waiting Logout Approval"
  | "Waiting Leave Approval"
  | "On Leave"
  | "Inactive"
  | "Duty On Hold"
  | "Resume Duty"
  | "On Permission";
  appStatus: string;
  loginTime: string;
  logoutTime: string;
  [key: string]: any;
}
const centeredFormStyle = {
  maxWidth: 320,
  margin: "0 auto",
};
const statusFilters = [
  { label: "Login", value: "All", color: "#18aee08e", text: "#fff" },
  { label: "Available", value: "Available", color: "#2b9348a1", text: "#fff" },
  { label: "On Trip", value: "On Trip", color: "#fcc049b2", text: "#fff" },
  { label: "On Leave", value: "On Leave", color: "#86398f98", text: "#fff" },
  { label: "On Permission", value: "On Permission", color: "#9540d180", text: "#fff" },
  { label: "Inactive", value: "Inactive", color: "#ef233ba9", text: "#fff" },
  { label: "Logout", value: "Logout", color: "#b3b0b0", text: "#fff" },
  // { label: "Duty On Hold", value: "Duty On Hold", color: "#da5552a0", text: "#fff" },
  // { label: "Resume Duty", value: "Resume Duty", color: "#d68c45a0", text: "#fff" },
  // { label: "Waiting Login", value: "Waiting Login Approval", color: "#0077b6a0", text: "#fff" },
  // { label: "Waiting Logout", value: "Waiting Logout Approval", color: "#023e8aa0", text: "#fff" },
  // { label: "Waiting Leave", value: "Waiting Leave Approval", color: "#6a4c93a0", text: "#fff" },
];

const PRIORITY_STATUS_CODES = new Set([1, 13, 15, 18]);

const rowClassName = (record: DriverRow) =>
  `row-${(record.driverStatus || "").replace(/\s/g, "")}`;

const tablePagination = {
  pageSizeOptions: ["5", "10", "100"],
  showSizeChanger: true,
  defaultPageSize: 10,
};

const tableScroll = {
  x: 1800,
  y: "calc(95vh - 320px)",
};

const DriverAttendance: React.FC = () => {
  const tableClass = "Live-table";
  const [activeFilter, setActiveFilter] = useState<string>("All");
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [data, setData] = useState<DriverRow[]>([]);
  const [selectedRowKey, setSelectedRowKey] = useState<React.Key | null>(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [approveForm] = Form.useForm();
  const [logoutForm] = Form.useForm();
  const [leaveForm] = Form.useForm();
  const [socketConnected, setSocketConnected] = useState(false);
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdForm] = Form.useForm();
  const [editLocationOpen, setEditLocationOpen] = useState(false);
  const [editLocationForm] = Form.useForm();

  const [locationDetail, setLocationDetail] = useState<{
    cabId: string;
    location: string;
  } | null>(null);
  const [pendingLocationEdit, setPendingLocationEdit] = useState<{ cabid: string; location: string } | null>(null);
  const [confirmLocationOpen, setConfirmLocationOpen] = useState(false);

  const [viewLocation, setViewLocation] = useState<{
    cabId: string;
    driverName: string;
    location: string;
  } | null>(null);

  const dispatch = useDispatch();
  const branchId = useBranchId();
  const {
    drivers,
    loadingDrivers,
    loading,
    message: successMessage,
    error,
    logoutLoading,
    logoutMessage,
    logoutError,
    leaveLoading,
    leaveMessage,
    leaveError,
    revertLogoutLoading,
    revertLogoutMessage,
    revertLogoutError,
    revertLoginLoading,
    revertLoginMessage,
    revertLoginError,
    logoutDetails,
    holdLoading,
    holdMessage,
    holdError,
    releaseLoading, releaseMessage, releaseError,
    resumeDutyLoading, resumeDutyMessage, resumeDutyError,
    editLocationLoading, editLocationMessage, editLocationError,
  } = useSelector((state: any) => state.driverAttendance || {});
  const { locations } = useSelector((state: any) => state.booking || {});

  const { user } = useSelector((state: any) => state.auth || {});

  const currentUsername =
    user?.username || localStorage.getItem("username") ||
    undefined;

  useEffect(() => {
    initSocket();

    setSocketConnected(socket.connected);

    const onConnect = () => setSocketConnected(true);
    const onDisconnect = () => setSocketConnected(false);

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);

    const updateDriver = (cabId: string, updater: (row: DriverRow) => DriverRow) => {
      setData(prev => {
        const idx = prev.findIndex(d => d.cabId === cabId);
        if (idx === -1) return prev;
        const next = prev.slice();
        next[idx] = updater(prev[idx]);
        return next;
      });
    };

    const onApproveAttendance = (payload: {
      cabId: string; openingKM: string; approvedBy: string; status: number; time: string;
    }) => {
      updateDriver(payload.cabId, d => ({
        ...d,
        statuscode: 11,
        driverStatus: "Available",
        openingKM: String(payload.openingKM),
        loginBy: payload.approvedBy,
      }));
    };

    const onCabLoginUpdate = (payload: {
      cabId: string; status: number; close_km: string; logoutby: string;
    }) => {
      updateDriver(payload.cabId, d => ({
        ...d,
        statuscode: 10,
        driverStatus: "Logout",
        closingKM: String(payload.close_km || ""),
        logoutby: payload.logoutby || d.logoutby,
      }));
    };

    const onDriverLogout = (payload: {
      cabId: string;
      close_km?: string; secondclose_km?: string; thirdclose_km?: string;
      LogoutTime?: string; secondLogoutTime?: string; thirdLogoutTime?: string;
      logoutby?: string; secondlogoutby?: string; thirdlogoutby?: string;
    }) => {
      const closingKM = payload.close_km || payload.secondclose_km || payload.thirdclose_km;
      const logoutTime = payload.LogoutTime || payload.secondLogoutTime || payload.thirdLogoutTime;
      const logoutBy = payload.logoutby || payload.secondlogoutby || payload.thirdlogoutby;

      updateDriver(payload.cabId, d => ({
        ...d,
        closingKM: closingKM ? String(closingKM) : d.closingKM,
        logoutTime: logoutTime
          ? new Date(logoutTime).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
          : d.logoutTime,
        logoutby: logoutBy || d.logoutby,
      }));
    };

    const onLoginReverted = (payload: { cab_id: string; status: number }) => {
      updateDriver(payload.cab_id, d => ({
        ...d,
        statuscode: 11,
        driverStatus: "Available",
      }));
    };

    const onLogoutReverted = (payload: { cab_id: string; status: number }) => {
      updateDriver(payload.cab_id, d => ({
        ...d,
        statuscode: 10,
        driverStatus: "Logout",
      }));
    };

    const onDutyOnHold = (payload: { cabId?: string; cab_id?: string; cabid?: string }) => {
      const id = payload.cabId || payload.cab_id || payload.cabid || "";
      updateDriver(id, d => ({
        ...d,
        statuscode: 17,
        driverStatus: "Duty On Hold",
      }));
    };

    const onCabResumedFromHold = (payload: {
      cab_id: string;
      isavailable: number;
      driver_status: string;
      status: number;
    }) => {
      updateDriver(payload.cab_id, d => ({
        ...d,
        statuscode: payload.status,
        driverStatus: "Available",
      }));
    };

    const onCabResumedDuty = (payload: {
      cab_id: string;
      status: number;
      driver_status: string;
    }) => {
      updateDriver(payload.cab_id, d => ({
        ...d,
        statuscode: payload.status,
        driverStatus: "Available",
      }));
    };

    const statusCodeToLabel = (code: number): DriverRow["driverStatus"] => {
      if (code === 1) return "Waiting Login Approval";
      if (code === 2 || code === 3 || code === 4) return "On Trip";
      if (code === 10) return "Logout";
      if (code === 11) return "Available";
      if (code === 13) return "Waiting Logout Approval";
      if (code === 14) return "Waiting Leave Approval";
      if (code === 15) return "On Leave";
      if (code === 16) return "Inactive";
      if (code === 17) return "Duty On Hold";
      if (code === 18) return "Resume Duty";
      if (code === 19) return "On Permission";
      return "Available";
    };

    const onCabDetailsUpdated = (payload: {
      cab_id: string;
      status: number;
      driver_status: string;
      isavailable: number;
    }) => {
      updateDriver(payload.cab_id, d => ({
        ...d,
        statuscode: payload.status,
        driverStatus: statusCodeToLabel(payload.status),
      }));
    };

    const onCabAvailable = (payload: {
      cab_id: string;
      status: number;
      driver_status: string;
      isavailable: number;
    }) => {
      updateDriver(payload.cab_id, d => ({
        ...d,
        statuscode: payload.status,
        driverStatus: "Available",
      }));
    };

    socket.on("cab_login_update", onCabLoginUpdate);
    socket.on("driver_logout", onDriverLogout);
    socket.on("driver_login_reverted", onLoginReverted);
    socket.on("driver_logout_reverted", onLogoutReverted);
    socket.on("cab_hold", (payload) => {
      //console.log("cab_hold received:", payload);
      onDutyOnHold(payload);
    });
    socket.on("cab_resumed_from_hold", onCabResumedFromHold);
    socket.on("cab_resumeduty", onCabResumedDuty);
    socket.on("approve_attendance", onApproveAttendance);
    socket.on("cabdetails_updated", onCabDetailsUpdated);
    socket.on("cab_available", onCabAvailable);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("cab_login_update", onCabLoginUpdate);
      socket.off("driver_logout", onDriverLogout);
      socket.off("driver_login_reverted", onLoginReverted);
      socket.off("driver_logout_reverted", onLogoutReverted);
      socket.off("cab_hold", onDutyOnHold);
      socket.off("cab_resumed_from_hold", onCabResumedFromHold);
      socket.off("cab_resumeduty", onCabResumedDuty);
      socket.off("approve_attendance", onApproveAttendance);
      socket.off("cabdetails_updated", onCabDetailsUpdated);
      socket.off("cab_available", onCabAvailable);
    };
  }, []);

  useMountEffectOnce(() => {
    dispatch(getDriverAttendanceRequest({ branchid: branchId, openingKM: "0" }));
  });

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        dispatch(getDriverAttendanceRequest({ branchid: branchId, openingKM: "0" }));
      }
    };

    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [dispatch, branchId]);

  useMountEffectOnce(() => {
    if (!locations?.length) {
      dispatch(getLocationsRequest(branchId));
    }
  });

  useEffect(() => {
    if (!editLocationMessage || !pendingLocationEdit) return;

    setData((currentData) =>
      currentData.map((driver) =>
        driver.cabId === pendingLocationEdit.cabid
          ? { ...driver, location: pendingLocationEdit.location }
          : driver
      )
    );
    message.success(editLocationMessage);
    setEditLocationOpen(false);
    setConfirmLocationOpen(false);
    editLocationForm.resetFields();
    setLocationDetail(null);
    setPendingLocationEdit(null);
    dispatch(getDriverAttendanceRequest({ branchid: branchId, openingKM: "0" }));
  }, [dispatch, editLocationForm, editLocationMessage, pendingLocationEdit, branchId]);

  useEffect(() => {
    if (editLocationError) {
      message.error(String(editLocationError));
      setPendingLocationEdit(null);
    }
  }, [editLocationError]);

  const handleFilterChange = useCallback((value: string) => {
    setActiveFilter(value);
    setSelectedRowKey(null);
  }, []);

  useEffect(() => {
    let driverArray = [];
    if (Array.isArray(drivers)) {
      driverArray = drivers;
    } else if (drivers?.data?.data && Array.isArray(drivers.data.data)) {
      driverArray = drivers.data.data;
    } else if (drivers?.data && Array.isArray(drivers.data)) {
      driverArray = drivers.data;
    }
    //console.log("Raw driver data:", driverArray[0]);

    if (driverArray && driverArray.length > 0) {
      const formattedData = driverArray.map((d: any, index: number) => {
        let dateStr = "-";
        let timeStr = "-";
        if (d.dateandtime) {
          const dt = new Date(d.dateandtime);
          dateStr = dt.toLocaleDateString("en-GB");
          timeStr = dt.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
        }

        return {
          ...d,
          key: d.cabid || index.toString(),
          cabId: d.cabid || "-",
          vehicle: d.vechicletype || d.vehicle || "-",
          driverName: d.drivername || d.driverName || "-",
          mobile: d.mobileno || d.mobile || "-",
          date: dateStr,
          time: timeStr,
          location: d.location || "-",
          active: d.blocked !== undefined ? !d.blocked : true,

          loginBy: d.loginby || "-",
          logoutBy: d.logoutby || "-",

          loginTime: "-",
          logoutTime: "-",

          appStatus: d.appstatus || d.appStatus || d.driverstatus || "-",

          openingKM: d.openingKM !== undefined ? String(d.openingKM) :
            d.openingkm !== undefined ? String(d.openingkm) : "",
          closingKM: d.closingKM !== undefined ? String(d.closingKM) :
            d.closingkm !== undefined ? String(d.closingkm) : "",

          driverStatus: (() => {
            const code = Number(d.statuscode);
            if (code === 1) return "Waiting Login Approval";
            if (code === 2 || code === 3 || code === 4) return "On Trip";
            if (code === 10) return "Logout";
            if (code === 11) return "Available";
            if (code === 13) return "Waiting Logout Approval";
            if (code === 14) return "Waiting Leave Approval";
            if (code === 15) return "On Leave";
            if (code === 16) return "Inactive";
            if (code === 17) return "Duty On Hold";
            if (code === 18) return "Resume Duty";
            if (code === 19) return "On Permission";
            return "Available";
          })(),
        };

      });
      setData(formattedData);
    } else {
      setData([]);
    }
  }, [drivers]);

  useEffect(() => {
    if (successMessage) {
      message.success(successMessage);
      setApproveOpen(false);
      approveForm.resetFields();
      dispatch({ type: "CLEAR_ATTENDANCE_MESSAGE" });
    }
  }, [successMessage, dispatch, approveForm]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch({ type: "CLEAR_ATTENDANCE_MESSAGE" });
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (logoutMessage) {
      message.success(logoutMessage);
      setLogoutOpen(false);
      logoutForm.resetFields();
      dispatch({ type: "CLEAR_ATTENDANCE_MESSAGE" });
      dispatch(getDriverAttendanceRequest({ branchid: branchId, openingKM: "0" }));
    }
  }, [logoutMessage, dispatch, logoutForm, branchId]);

  useEffect(() => {
    if (logoutError) {
      message.error(logoutError);
      dispatch({ type: "CLEAR_ATTENDANCE_MESSAGE" });
    }
  }, [logoutError, dispatch]);

  useEffect(() => {
    if (leaveMessage) {
      message.success(leaveMessage);
      setLeaveOpen(false);
      leaveForm.resetFields();
      dispatch({ type: "CLEAR_ATTENDANCE_MESSAGE" });
      dispatch(getDriverAttendanceRequest({ branchid: branchId, openingKM: "0" }));
    }
  }, [leaveMessage, dispatch, leaveForm, branchId]);

  useEffect(() => {
    if (leaveError) {
      message.error(leaveError);
      dispatch({ type: "CLEAR_ATTENDANCE_MESSAGE" });
    }
  }, [leaveError, dispatch]);

  useEffect(() => {
    if (revertLogoutMessage) {
      message.success(revertLogoutMessage);
      setSelectedRowKey(null);
      setActiveFilter("All");
      dispatch({ type: "CLEAR_ATTENDANCE_MESSAGE" });
      dispatch(getDriverAttendanceRequest({ branchid: branchId, openingKM: "0" }));
    }
  }, [revertLogoutMessage, dispatch, branchId]);

  useEffect(() => {
    if (revertLogoutError) {
      message.error(revertLogoutError);
      dispatch({ type: "CLEAR_ATTENDANCE_MESSAGE" });
    }
  }, [revertLogoutError, dispatch]);

  useEffect(() => {
    if (revertLoginMessage) {
      message.success(revertLoginMessage);
      setSelectedRowKey(null);
      setActiveFilter("All");
      dispatch({ type: "CLEAR_ATTENDANCE_MESSAGE" });
      dispatch(getDriverAttendanceRequest({ branchid: branchId, openingKM: "0" }));
    }
  }, [revertLoginMessage, dispatch, branchId]);

  useEffect(() => {
    if (revertLoginError) {
      message.error(revertLoginError);
      dispatch({ type: "CLEAR_ATTENDANCE_MESSAGE" });
    }
  }, [revertLoginError, dispatch]);

  useEffect(() => {
    if (logoutDetails) {
      const details = Array.isArray(logoutDetails) ? logoutDetails[0] : logoutDetails;
      if (details) {
        const newValues: any = {};

        const apiStarting = details.openingKM ?? details.OpeningKM ?? details.openingkm;
        if (apiStarting !== null && apiStarting !== undefined) {
          newValues.startingKM = String(apiStarting);
        }

        const apiClosing = details.closingKM ?? details.closingkm ?? details.TotalKM;
        const apiOpening = details.openingKM ?? details.OpeningKM ?? details.openingkm;

        if (
          apiClosing !== null &&
          apiClosing !== undefined &&
          apiClosing !== 0 &&
          apiClosing !== "" &&
          String(apiClosing) !== String(apiOpening)
        ) {
          newValues.closingKM = String(apiClosing);
        } else {
          newValues.closingKM = "";
        }

        if (details.Amount !== null && details.Amount !== undefined) {
          newValues.amount = details.Amount;
        }

        const timeStr = details.LoginTime || details.logintime;
        if (timeStr && timeStr !== "-") {
          const parsed = dayjs(timeStr, "HH:mm").isValid()
            ? dayjs(timeStr, "HH:mm")
            : dayjs(timeStr);
          if (parsed.isValid()) newValues.loginTime = parsed;
        }

        logoutForm.setFieldsValue(newValues);
      }
    }
  }, [logoutDetails, logoutForm]);

  useEffect(() => {
    if (holdMessage) {
      message.success(holdMessage);
      setHoldOpen(false);
      holdForm.resetFields();
      dispatch({ type: "CLEAR_ATTENDANCE_MESSAGE" });
    }
  }, [holdMessage, dispatch, holdForm]);

  useEffect(() => {
    if (holdError) {
      message.error(holdError);
      dispatch({ type: "CLEAR_ATTENDANCE_MESSAGE" });
    }
  }, [holdError, dispatch]);

  useEffect(() => {
    if (releaseMessage) {
      message.success(releaseMessage);
      setSelectedRowKey(null);
      dispatch({ type: "CLEAR_ATTENDANCE_MESSAGE" });
      dispatch(getDriverAttendanceRequest({ branchid: branchId, openingKM: "0" }));
    }
  }, [releaseMessage, dispatch, branchId]);

  useEffect(() => {
    if (releaseError) {
      message.error(releaseError);
      dispatch({ type: "CLEAR_ATTENDANCE_MESSAGE" });
    }
  }, [releaseError, dispatch]);

  useEffect(() => {
    if (resumeDutyMessage) {
      message.success(resumeDutyMessage);
      setSelectedRowKey(null);
      dispatch({ type: "CLEAR_ATTENDANCE_MESSAGE" });
      dispatch(getDriverAttendanceRequest({ branchid: branchId, openingKM: "0" }));
    }
  }, [resumeDutyMessage, dispatch, branchId]);

  useEffect(() => {
    if (resumeDutyError) {
      message.error(resumeDutyError);
      dispatch({ type: "CLEAR_ATTENDANCE_MESSAGE" });
    }
  }, [resumeDutyError, dispatch]);

  const attendanceMenu = useMemo(() => {
    const s = data.find(d => d.key === selectedRowKey)?.driverStatus;

    if (s === "Available") return [{ key: "1", label: "Revert Login" }];
    if (s === "Logout") return [{ key: "2", label: "Revert Logout" }];
    if (s === "On Trip") return [{ key: "3", label: "Mark Available" }];

    return [
      { key: "1", label: "Revert Login" },
      { key: "2", label: "Revert Logout" },
      { key: "3", label: "Mark Available" },
    ];
  }, [selectedRowKey, data]);

  const selectedDriverStatus = useMemo(() => {
    if (!selectedRowKey) return null;
    return data.find(d => d.key === selectedRowKey)?.driverStatus ?? null;
  }, [selectedRowKey, data]);

  const s = selectedDriverStatus;

  const canApprove = !!selectedRowKey && (s === "Waiting Login Approval" || s === "Logout" || s === "Waiting Leave Approval" || s === "On Leave");
  const canLogout = !!selectedRowKey && (s === "Available" || s === "Waiting Logout Approval");
  const canLeave = !!selectedRowKey && s === "Logout";
  const canAttMgr = !!selectedRowKey && (s === "Logout" || s === "On Trip" || s === "Available");
  const canHold = !!selectedRowKey && s === "Available";
  const canRelease = !!selectedRowKey && s === "Duty On Hold";
  const canResume = !!selectedRowKey && (s === "On Permission" || s === "Resume Duty");

  const rowSelection = useMemo(() => ({
    type: "radio" as const,
    selectedRowKeys: selectedRowKey ? [selectedRowKey] : [],
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRowKey(selectedRowKeys[0]);
    },
  }), [selectedRowKey]);

  const filteredData = useMemo(() => {
    const term = deferredSearch.trim().toLowerCase();

    const filtered = data.filter((row) => {
      const matchesSearch =
        !term ||
        row.cabId.toLowerCase().includes(term) ||
        row.driverName.toLowerCase().includes(term) ||
        row.mobile.includes(term);

      const matchesStatus =
        activeFilter === "All" || row.driverStatus === activeFilter;

      return matchesSearch && matchesStatus;
    });

    return filtered.sort((a, b) => {
      const aPriority = PRIORITY_STATUS_CODES.has(Number(a.statuscode)) ? 0 : 1;
      const bPriority = PRIORITY_STATUS_CODES.has(Number(b.statuscode)) ? 0 : 1;
      return aPriority - bPriority;
    });
  }, [data, deferredSearch, activeFilter]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { All: data.length };

    data.forEach((row) => {
      counts[row.driverStatus] = (counts[row.driverStatus] || 0) + 1;
    });

    return counts;
  }, [data]);

  const modalTitle = (text: string) => (
    <div
      style={{
        textAlign: "center",
        background: "#86398f",
        color: "#fff",
        padding: "10px",
        borderRadius: 8,
        fontWeight: 700,
        fontSize: 18,
      }}
    >
      {text}
    </div>
  );

  const handleApproveClick = useCallback(() => {
    if (!selectedRowKey) {
      message.warning("Please select a driver to approve.");
      return;
    }
    const selectedRecord = data.find(d => d.key === selectedRowKey);
    approveForm.setFieldsValue({
      cabid: selectedRecord?.cabId,
      previousClosingKM: selectedRecord?.closingKM || "",
      openingKM: selectedRecord?.openingKM || "",
      date: dayjs(),
      requestedTime: dayjs(),
      time: dayjs(),
    });
    setApproveOpen(true);
  }, [selectedRowKey, data, approveForm]);

  const handleLogoutClick = useCallback(() => {
    if (!canLogout) {
      message.warning("Please select a driver to logout.");
      return;
    }
    const selectedRecord = data.find(d => d.key === selectedRowKey);
    if (selectedRecord) {
      let loginTimeValue = null;
      if (selectedRecord.loginTime && selectedRecord.loginTime !== "-") {
        loginTimeValue = dayjs(selectedRecord.loginTime, "HH:mm");
      } else if (selectedRecord.dateandtime) {
        loginTimeValue = dayjs(selectedRecord.dateandtime);
      }

      logoutForm.setFieldsValue({
        cabid: selectedRecord.cabId,
        date: dayjs(),
        startingKM: selectedRecord.openingKM || "",
        closingKM: "",
        loginTime: loginTimeValue,
        time: dayjs(),
      });
      // dispatch(getLogoutDetailsRequest(selectedRecord.cabId));
    }
    setLogoutOpen(true);
  }, [canLogout, selectedRowKey, data, logoutForm]);

  const handleLeaveClick = useCallback(() => {
    if (!selectedRowKey) {
      message.warning("Please select a driver for leave.");
      return;
    }
    const selectedRecord = data.find(d => d.key === selectedRowKey);
    leaveForm.setFieldsValue({
      cabid: selectedRecord?.cabId,
    });
    setLeaveOpen(true);
  }, [selectedRowKey, data, leaveForm]);

  const handleMenuClick = useCallback((e: { key: string }) => {
    if (!selectedRowKey) {
      message.warning("Please select a driver.");
      return;
    }
    const selectedRecord = data.find(d => d.key === selectedRowKey);
    if (!selectedRecord) return;

    if (e.key === "1") {
      dispatch(revertLogoutRequest(selectedRecord.cabId, currentUsername));
    }
    if (e.key === "2") {
      dispatch(revertLoginRequest(selectedRecord.cabId, currentUsername));
    }
    if (e.key === "3") {
      dispatch(makeCabAvailableRequest(selectedRecord.cabId));
    }
  }, [selectedRowKey, data, dispatch, currentUsername]);

  const handleHoldClick = useCallback(() => {
    if (!selectedRowKey) {
      message.warning("Please select a driver to hold.");
      return;
    }
    const selectedRecord = data.find(d => d.key === selectedRowKey);
    holdForm.setFieldsValue({
      cabid: selectedRecord?.cabId,
      reason: undefined,
    });
    setHoldOpen(true);
  }, [selectedRowKey, data, holdForm]);

  const handleReleaseClick = useCallback(() => {
    if (!selectedRowKey) {
      message.warning("Please select a driver to release.");
      return;
    }
    const selectedRecord = data.find(d => d.key === selectedRowKey);
    if (selectedRecord) {
      dispatch(releaseHoldCabRequest({
        cabid: selectedRecord.cabId,
        username: currentUsername,
      }));
    }
  }, [selectedRowKey, data, dispatch, currentUsername]);

  const handleResumeClick = useCallback(() => {
    if (!selectedRowKey) {
      message.warning("Please select a driver to resume.");
      return;
    }
    const selectedRecord = data.find(d => d.key === selectedRowKey);
    if (selectedRecord) {
      dispatch(resumeDutyCabRequest(selectedRecord.cabId, currentUsername));
    }
  }, [selectedRowKey, data, dispatch, currentUsername]);

  const columns: ColumnsType<DriverRow> = useMemo(() => [
    {
      title: (
        <Space size={6}>
          <span className="icon-wrapper">
            <CarTaxiFront size={18} color="#fff" />
          </span>
          Cab ID
        </Space>
      ),
      dataIndex: "cabId",
      align: "center",
      width: 100,
    },
    {
      title: (
        <Space size={6}>
          <span className="icon-wrapper">
            <Car size={18} color="#fff" />
          </span>
          Vehicle
        </Space>
      ),
      dataIndex: "vehicle",
      align: "center",
      width: 100,
    },
    {
      title: (
        <Space size={6}>
          <span className="icon-wrapper">
            <User size={18} color="#fff" />
          </span>
          Driver Name
        </Space>
      ),
      dataIndex: "driverName",
      align: "center",
      width: 150,
    },
    {
      title: (
        <Space size={6}>
          <span className="icon-wrapper">
            <Phone size={18} color="#fff" />
          </span>
          Mobile
        </Space>
      ),
      dataIndex: "mobile",
      align: "center",
      width: 120,
    },
    {
      title: (
        <Space size={6}>
          <span className="icon-wrapper">
            <Calendar size={18} color="#fff" />
          </span>
          Date
        </Space>
      ),
      dataIndex: "date",
      align: "center",
      width: 120,
    },
    {
      title: (
        <Space align="center" size={6}>
          <Clock size={18} color="white" strokeWidth={2} />
          Time
        </Space>
      ),
      dataIndex: "time",
      align: "center",
      width: 120,
    },
    {
      title: (
        <Space align="center" size={6}>
          <MapPin size={18} color="white" strokeWidth={2} />
          Location
        </Space>
      ),
      dataIndex: "live_location",
      key: "live_location",
      align: "center",
      width: 200,
      ellipsis: true,
      render: (location: string, record: DriverRow) => (
        <Space size={6} style={{ maxWidth: "100%" }}>
          <Tooltip title={location || "-"} placement="topLeft">
            <span
              style={{
                display: "inline-block",
                maxWidth: 150,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {location || "-"}
            </span>
          </Tooltip>

          {record.driverStatus !== "Available" && (
            <Tooltip title="View full location">
              <Button
                type="text"
                size="small"
                className="loc-more-btn"
                aria-label={`View full location for ${record.cabId}`}
                icon={<InfoCircleOutlined />}
                onClick={() =>
                  setViewLocation({
                    cabId: record.cabId,
                    driverName: record.driverName,
                    location: location || "-",
                  })
                }
              />
            </Tooltip>
          )}

          {record.driverStatus === "Available" && (
            <Tooltip title="Change location">
              <Button
                type="text"
                size="small"
                className="loc-edit-btn"
                aria-label={`Edit location for ${record.cabId}`}
                icon={<InfoCircleOutlined size={14} />}
                onClick={() => {
                  if (!locations?.length) dispatch(getLocationsRequest(branchId));

                  editLocationForm.setFieldsValue({
                    cabid: record.cabId,
                    location: location === "-" ? undefined : location,
                  });

                  setLocationDetail({
                    cabId: record.cabId,
                    location: location || "-",
                  });
                  setEditLocationOpen(true);
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "driverStatus",
      align: "center",
      width: 200,
      render: (status: string) => {
        const colorMap: Record<string, string> = {
          "Available": "#2b9348",
          "On Trip": "#f4a700",
          "Logout": "#b3b0b0",
          "On Leave": "#86398f",
          "On Permission": "#9540d1",
          "Inactive": "#ef233b",
          "Duty On Hold": "#da5552",
          "Resume Duty": "#d68c45",
          "Waiting Login Approval": "#0077b6",
          "Waiting Logout Approval": "#023e8a",
          "Waiting Leave Approval": "#6a4c93",
        };
        return (
          <Tag color={colorMap[status] || "#999"} style={{ fontWeight: 600 }}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: (
        <Space align="center" size={6}>
          <LogIn size={16} />
          Login
        </Space>
      ),
      dataIndex: "loginby",
      align: "center",
      width: 150,
      render: (loginby: string, record: any) => (
        <div>
          <div>{loginby}</div>
          <div>KM - {record.openingKM}</div>
        </div>
      ),
    },
    {
      title: (
        <Space align="center" size={6}>
          <LogIn size={16} />
          Logout
        </Space>
      ),
      dataIndex: "logoutby",
      align: "center",
      width: 150,
      render: (logoutby: string, record: any) => (
        <div>
          <div>{logoutby}</div>
          <div>KM - {record.closingKM}</div>
        </div>
      ),
    },
  ], [locations, dispatch, editLocationForm, branchId]);

  const tableNode = useMemo(() => (
    <Table
      bordered
      className={tableClass}
      columns={columns}
      dataSource={filteredData}
      rowSelection={rowSelection}
      loading={loadingDrivers}
      rowClassName={rowClassName}
      scroll={tableScroll}
      pagination={tablePagination}
    />
  ), [columns, filteredData, rowSelection, loadingDrivers]);

  return (
    <div style={styles.page}>
      <Card style={styles.headerCard} bodyStyle={{ padding: "10px 32px" }}>
        <Row align="middle" justify="space-between" gutter={16}>
          <Col>
            <Space align="center" size={12}>
              <CarTaxiFront size={28} strokeWidth={1.8} color={BG_PAGE} />
              <div>
                <Title level={3} style={{ margin: 0, color: BG_PAGE }}>
                  Driver Attendance Dashboard
                </Title>
                <Space size={8}>
                  <Text type="secondary" style={{ fontSize: 12, color: BG_PAGE }}>
                    Monitor • Filter • Manage driver activity
                  </Text>
                  <Space size={4} align="center">
                    {socketConnected
                      ? <Wifi size={13} color="#7dff8a" />
                      : <WifiOff size={13} color="#ff7d7d" />}
                    <Text style={{ fontSize: 11, color: socketConnected ? "#7dff8a" : "#ff7d7d" }}>
                      {socketConnected ? "Live" : "Offline"}
                    </Text>
                  </Space>
                </Space>
              </div>
            </Space>
          </Col>


          <Col>
            <Input
              prefix={<Search size={18} color={TEXT_SECONDARY} />}
              placeholder="Search cab, owner, mobile..."
              size="large"
              style={{ width: "100%", maxWidth: 340, minWidth: 200, borderRadius: 12 }}
              allowClear
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>
        </Row>
      </Card>

      <Card
        className="da-button-card"
        style={{
          borderRadius: 16,
          marginBottom: 10,
        }}
        bodyStyle={{ padding: 20 }}
      >
        <Row
          className="da-btn-row"
          gutter={[
            { xs: 8, xl: 12, xxl: 20 },
            { xs: 8, xl: 8, xxl: 20 },
          ]}
          align="middle"
        >
          {/* <Col>
            <Button
              disabled={!canApprove}
              onClick={handleApproveClick}
              style={{
                background: canApprove ? "#5d9789ff" : "#b0bec5",
                color: "#fff",
                border: "none",
                borderRadius: 10,
                height: 40,
                padding: "0 22px",
                fontWeight: 600,
                opacity: !canApprove ? 0.5 : 1,
                cursor: !canApprove ? "not-allowed" : "pointer",
              }}
            >
              Approve
            </Button>
          </Col>

          <Col>
            <Button
              disabled={!canLogout}
              onClick={handleLogoutClick}
              style={{
                background: canLogout ? "#a68a64" : "#b0bec5",
                color: "#fff",
                border: "none",
                borderRadius: 10,
                height: 40,
                padding: "0 22px",
                fontWeight: 600,
                opacity: !canLogout ? 0.5 : 1,
                cursor: !canLogout ? "not-allowed" : "pointer",
              }}
            >
              Logout
            </Button>
          </Col>

          <Col>
            <Button
              disabled={!canLeave}
              onClick={handleLeaveClick}
              style={{
                background: canLeave ? "#0a9496f1" : "#b0bec5",
                color: "#ffffff",
                border: "none",
                borderRadius: 10,
                height: 40,
                padding: "0 22px",
                fontWeight: 600,
                opacity: !canLeave ? 0.5 : 1,
                cursor: !canLeave ? "not-allowed" : "pointer",
              }}
            >
              Leave
            </Button>
          </Col>

          <Col>
            <Dropdown
              menu={{
                items: attendanceMenu,
                onClick: handleMenuClick,
              }}
              placement="bottomLeft"
              disabled={!canAttMgr}
            >
              <Button
                style={{
                  background: canAttMgr ? "#86398fd7" : "#b0bec5",
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  height: 40,
                  padding: "0 22px",
                  fontWeight: 600,
                  opacity: !canAttMgr ? 0.5 : 1,
                }}
              >
                Attendance Manager ▾
              </Button>
            </Dropdown>
          </Col>

          <Col>
            <Button
              disabled={!canHold}
              onClick={handleHoldClick}
              style={{
                background: canHold ? "#da5452cc" : "#b0bec5",
                color: "#fff",
                border: "none",
                borderRadius: 10,
                height: 40,
                padding: "0 22px",
                fontWeight: 600,
                opacity: !canHold ? 0.5 : 1,
                cursor: !canHold ? "not-allowed" : "pointer",
              }}
            >
              Hold
            </Button>
          </Col>

          <Col>
            <Button
              disabled={!canRelease}
              onClick={handleReleaseClick}
              loading={releaseLoading}
              style={{
                background: canRelease ? "#52b67cc7" : "#b0bec5",
                color: "#fff",
                border: "none",
                borderRadius: 10,
                height: 40,
                padding: "0 22px",
                fontWeight: 600,
                opacity: !canRelease ? 0.5 : 1,
                cursor: !canRelease ? "not-allowed" : "pointer",
              }}
            >
              Release
            </Button>
          </Col>

          <Col>
            <Button
              disabled={!canResume}
              onClick={handleResumeClick}
              loading={resumeDutyLoading}
              style={{
                background: canResume ? "#d68c45" : "#b0bec5",
                color: "#fff",
                border: "none",
                borderRadius: 10,
                height: 40,
                padding: "0 22px",
                fontWeight: 600,
                opacity: !canResume ? 0.5 : 1,
                cursor: !canResume ? "not-allowed" : "pointer",
              }}
            >
              Resume
            </Button>
          </Col> */}
          <Col>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg,#4e2f73,#86398f)",
                borderRadius: 14,
                padding: "6px 18px",
                minWidth: 100,
                height: 42,
                boxShadow: "0 4px 14px rgba(134,57,143,0.45)",
                border: "1.5px solid rgba(255,255,255,0.18)",
                gap: 0,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20 }}>
                <span style={{ fontSize: 14, color: "rgba(255,255,255,0.75)", fontWeight: 600, letterSpacing: "0.8px", textTransform: "uppercase", lineHeight: 1 }}>
                  Total Vehicles
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
                  <CarTaxiFront size={15} color="#e0b3e8" strokeWidth={2} />
                  <span style={{ fontSize: 18, fontWeight: 800, color: "#fff", lineHeight: 1 }}>{statusCounts["All"] || 0}</span>
                </div>
              </div>

            </div>
          </Col>

        </Row>

        <Row
          className="da-btn-row da-filter-row"
          gutter={[
            { xs: 8, xl: 12, xxl: 20 },
            { xs: 8, xl: 8, xxl: 20 },
          ]}
          style={{ marginTop: "clamp(8px, 1vw, 16px)" }}
          align="middle"
        >
          {statusFilters.map((item) => {
            const active = activeFilter === item.value;
            return (
              <Col key={item.value}>
                <Button
                  onClick={() => handleFilterChange(item.value)}
                  style={{
                    background: item.color,
                    color: item.text,
                    border:
                      item.color === "#ffffff" ? "1px solid #d9d9d9" : "none",
                    borderRadius: 12,
                    height: 42,
                    padding: "0 20px",
                    fontWeight: 600,
                    transform: active ? "translateY(-2px)" : "none",
                    boxShadow: active
                      ? "0 8px 18px rgba(0, 0, 0, 0.47)"
                      : "0 2px 6px rgba(0, 0, 0, 0.45)",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {item.label}

                  <span
                    className="da-count"
                    style={{
                      color: "#ffffff",
                      borderRadius: 20,
                      padding: "2px 10px",
                      fontSize: 16,
                      fontWeight: 800,
                      minWidth: 26,
                      textAlign: "center",
                    }}
                  >
                    {statusCounts[item.value] || 0}
                  </span>
                </Button>
              </Col>
            );
          })}
        </Row>
      </Card>

      <div style={{ flex: 1, minHeight: 0, minWidth: 0 }}>
        {tableNode}
      </div>

      <Modal
        open={approveOpen}
        onCancel={() => setApproveOpen(false)}
        footer={null}
        width={420}
        title={modalTitle("Approve")}
      >
        <Form
          form={approveForm}
          layout="vertical"
          style={centeredFormStyle}
          onFinish={(values) => {
            dispatch(approveAttendanceRequest(values.cabid, values.openingKM, currentUsername));
          }}
        >
          <Form.Item label="Cab ID" name="cabid" rules={[{ required: true, message: 'Please input cab ID!' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Date">
            <DatePicker style={{ width: "100%" }} defaultValue={dayjs()} disabled />
          </Form.Item>

          <Form.Item label="Requested Time">
            <TimePicker style={{ width: "100%" }} format="HH:mm"
              defaultValue={dayjs()} disabled />
          </Form.Item>

          <Form.Item label="Previous Closing KM" name="previousClosingKM">
            <Input readOnly style={{ background: "#f5f5f5" }} />
          </Form.Item>

          <Form.Item label="Opening KM" name="openingKM" rules={[{ required: true, message: 'Please input opening KM!' }]}>
            <Input />
          </Form.Item>

          <Form.Item label="Time">
            <TimePicker style={{ width: "100%" }} format="HH:mm"
              defaultValue={dayjs()} disabled />
          </Form.Item>

          <div style={{ textAlign: "center", marginTop: 16 }}>
            <Button type="primary" htmlType="submit" loading={loading}>Approve</Button>
            <Button
              style={{ marginLeft: 10 }}
              onClick={() => setApproveOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        open={leaveOpen}
        onCancel={() => setLeaveOpen(false)}
        footer={null}
        width={420}
        title={modalTitle("Leave")}
      >
        <Form
          form={leaveForm}
          layout="vertical"
          style={centeredFormStyle}
          onFinish={(values) => {
            const fromdate = values.dateRange ? values.dateRange[0].format("YYYY-MM-DD") : "";
            const todate = values.dateRange ? values.dateRange[1].format("YYYY-MM-DD") : "";
            dispatch(leaveApproveRequest(values.cabid, values.reason, fromdate, todate));
          }}
        >
          <Form.Item label="Cab ID" name="cabid" rules={[{ required: true, message: 'Please input cab ID!' }]}>
            <Input />
          </Form.Item>

          <Form.Item label="Date Range" name="dateRange" rules={[{ required: true, message: 'Please select date range!' }]}>
            <DatePicker.RangePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item label="Reason" name="reason" rules={[{ required: true, message: 'Please select reason!' }]}>
            <Select
              options={[
                { label: "Maintenance", value: "maintenance" },
                { label: "Medical Leave", value: "medical" },
                { label: "Personal Leave", value: "personal" },
                { label: "Others", value: "others" },
              ]}
            />
          </Form.Item>

          <div style={{ textAlign: "center", marginTop: 16 }}>
            <Button type="primary" htmlType="submit" loading={leaveLoading}>Approve</Button>
            <Button
              style={{ marginLeft: 10 }}
              onClick={() => setLeaveOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        open={logoutOpen}
        onCancel={() => setLogoutOpen(false)}
        footer={null}
        width={420}
        title={modalTitle("Logout")}
      >
        <Form
          form={logoutForm}
          layout="vertical"
          style={centeredFormStyle}
          onFinish={(values) => {
            dispatch(approveLogoutRequest(values.cabid, values.closingKM, currentUsername));
          }}
        >
          <Form.Item label="Cab ID" name="cabid" rules={[{ required: true, message: 'Please input cab ID!' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Date" name="date">
            <DatePicker style={{ width: "100%" }} defaultValue={dayjs()} disabled />
          </Form.Item>

          <Form.Item label="Login Time" name="loginTime">
            <TimePicker style={{ width: "100%" }} format="HH:mm:ss" disabled />
          </Form.Item>

          <Form.Item label="Starting KM" name="startingKM">
            <Input readOnly style={{ background: "#f5f5f5" }} />
          </Form.Item>

          <Form.Item label="Closing KM" name="closingKM" rules={[{ required: true, message: 'Please input closing KM!' }]}>
            <Input />
          </Form.Item>

          <Form.Item label="Amount" name="amount">
            <Input readOnly style={{ background: "#f5f5f5" }} />
          </Form.Item>

          <Form.Item label="Time" name="time">
            <TimePicker style={{ width: "100%" }} format="HH:mm"
              defaultValue={dayjs()} disabled />
          </Form.Item>

          <div style={{ textAlign: "center", marginTop: 16 }}>
            <Button type="primary" htmlType="submit" loading={logoutLoading}>Approve</Button>
            <Button
              style={{ marginLeft: 10 }}
              onClick={() => setLogoutOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        open={holdOpen}
        onCancel={() => {
          setHoldOpen(false);
          holdForm.resetFields();
        }}
        footer={null}
        width={420}
        title={modalTitle("Hold Duty")}
      >
        <Form
          form={holdForm}
          layout="vertical"
          style={centeredFormStyle}
          onFinish={(values) => {
            dispatch(holdDutyRequest(values.cabid, currentUsername, values.reason));
          }}
        >
          <Form.Item label="Cab ID" name="cabid" rules={[{ required: true, message: 'Please input cab ID!' }]}>
            <Input readOnly style={{ background: "#f5f5f5" }} />
          </Form.Item>

          <Form.Item label="Reason" name="reason" rules={[{ required: true, message: 'Please select a reason!' }]}>
            <Select
              placeholder="Select reason"
              options={[
                { label: "Break", value: "break" },
                { label: "Maintenance", value: "maintenance" },
                { label: "Personal", value: "personal" },
                { label: "Others", value: "others" },
              ]}
            />
          </Form.Item>

          <div style={{ textAlign: "center", marginTop: 16 }}>
            <Button type="primary" htmlType="submit" loading={holdLoading} danger>
              Confirm Hold
            </Button>
            <Button
              style={{ marginLeft: 10 }}
              onClick={() => {
                setHoldOpen(false);
                holdForm.resetFields();
              }}
            >
              Cancel
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        open={editLocationOpen}
        centered
        width={{ xs: "94vw", sm: 520 }}
        className="loc-modal"
        onCancel={() => {
          setEditLocationOpen(false);
          setConfirmLocationOpen(false);
          editLocationForm.resetFields();
          setLocationDetail(null);
        }}
        title={
          <Space align="center" size={12}>
            <span className="loc-modal-icon">
              <MapPin size={20} strokeWidth={2.2} />
            </span>
            <span>
              <span className="loc-modal-heading">Update Location</span>
              <span className="loc-modal-cab">{locationDetail?.cabId || "-"}</span>
            </span>
          </Space>
        }
        styles={{
          header: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            background: "linear-gradient(135deg, #89339C, #A74EBB)",
            borderBottom: "none",
            padding: "18px 24px",
            marginBottom: 0,
          },
          title: { color: "#ffffff", margin: 0 },
          body: { padding: "22px 24px 4px" },
          footer: {
            margin: 0,
            padding: "14px 24px 18px",
            borderTop: "1px solid #f0e9f3",
          },
        }}
        modalRender={(modal) => (
          <div style={{ borderRadius: 16, overflow: "hidden" }}>{modal}</div>
        )}
        footer={
          <div className="loc-modal-actions">
            <Button
              type="default"
              size="large"
              style={{ minWidth: 120, borderRadius: 8, fontWeight: 500 }}
              onClick={() => {
                setEditLocationOpen(false);
                setConfirmLocationOpen(false);
                editLocationForm.resetFields();
                setLocationDetail(null);
                message.info("No changes");
              }}
            >
              Cancel
            </Button>

            <Popconfirm
              open={confirmLocationOpen}
              title="Confirm location change"
              description="Do you want to update this driver's location?"
              okText="Yes"
              cancelText="No"
              placement="topRight"
              onConfirm={() => {
                setConfirmLocationOpen(false);
                editLocationForm.submit();
              }}
              onCancel={() => {
                setConfirmLocationOpen(false);
                message.info("No changes");
              }}
            >
              <Button
                type="primary"
                size="large"
                loading={editLocationLoading}
                style={{
                  minWidth: 120,
                  borderRadius: 8,
                  background: "linear-gradient(135deg, #89339c, #b56ac8)",
                  border: "none",
                  fontWeight: 600,
                  boxShadow: "0 4px 12px rgba(137, 67, 154, 0.3)",
                }}
                onClick={() => {
                  editLocationForm
                    .validateFields()
                    .then(() => setConfirmLocationOpen(true))
                    .catch(() => setConfirmLocationOpen(false));
                }}
              >
                Save
              </Button>
            </Popconfirm>
          </div>
        }
      >
        <div className="loc-field-label">Current location</div>
        <div className="loc-current-card">
          <MapPin size={16} strokeWidth={2.2} color={PRIMARY} className="loc-current-icon" />
          <div className="loc-current-text">{locationDetail?.location || "-"}</div>
        </div>

        <Divider className="loc-modal-divider">
          <ArrowDown size={16} strokeWidth={2.2} color="#b9a3c0" />
        </Divider>

        <Form
          form={editLocationForm}
          name="editDriverLocation"
          layout="vertical"
          requiredMark={false}
          onFinish={(values) => {
            const selectedLocation = locations?.find(
              (location: any) => location.PlaceName === values.location
            );

            if (!selectedLocation) {
              message.error("Please select a valid location.");
              return;
            }

            if (!currentUsername) {
              message.error("Unable to determine the logged-in user.");
              return;
            }

            setPendingLocationEdit({ cabid: values.cabid, location: values.location });
            dispatch(editDriverLocationRequest({
              cabid: values.cabid,
              piclat: String(selectedLocation.Latitude ?? ""),
              piclon: String(selectedLocation.Longitude ?? ""),
              present_location: values.location,
              locationupdatedby: currentUsername,
            }));
          }}
        >
          <Form.Item name="cabid" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            label={<span className="loc-field-label">New location</span>}
            name="location"
            rules={[{ required: true, message: "Please select a location." }]}
          >
            <Select
              showSearch
              size="large"
              placeholder="Search and select a location"
              optionFilterProp="children"
              style={{ width: "100%" }}
            >
              {(locations || []).map((location: any) => (
                <Select.Option key={location.ID} value={location.PlaceName}>
                  {location.PlaceName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={!!viewLocation}
        centered
        width={{ xs: "94vw", sm: 520 }}
        className="loc-modal"
        onCancel={() => setViewLocation(null)}
        title={
          <Space align="center" size={12}>
            <span className="loc-modal-icon">
              <MapPin size={20} strokeWidth={2.2} />
            </span>
            <span>
              <span className="loc-modal-heading">Location Details</span>
              <span className="loc-modal-cab">{viewLocation?.cabId || "-"}</span>
            </span>
          </Space>
        }
        styles={{
          header: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            background: "linear-gradient(135deg, #89339C, #A74EBB)",
            padding: "18px 24px",
            margin: 0,
          },
          body: { padding: "22px 24px 8px" },
          footer: { padding: "14px 24px 20px", margin: 0 },
        }}
        footer={
          <div className="loc-modal-actions">
            <Button onClick={() => setViewLocation(null)}>Close</Button>
          </div>
        }
      >
        <div className="loc-field-label">Current location</div>
        <div className="loc-current-card">
          <span className="loc-current-icon">
            <MapPin size={16} color={PRIMARY} />
          </span>
          <div className="loc-current-text">{viewLocation?.location || "-"}</div>
        </div>
      </Modal>

      <style>
        {`
    .icon-wrapper { padding:4px; background:${PRIMARY}; border-radius:6px;}
    th.ant-table-cell { background:${PRIMARY}!important; color:#fff!important; }


    /* ── Action + status filter rows: one line each down to ~1280px ─────────
       The two rows measure roughly 1185px and 1235px against about 1195px of
       content width on a 1366 laptop, so both spilled onto a second line. The
       buttons carry their padding and font size as inline styles, so trimming
       them takes !important; every clamp max is the value already rendered, so
       a desktop monitor is unchanged. Horizontal gutters are handled by the
       responsive gutter props on the Rows themselves. */
    .da-btn-row .ant-btn {
      padding-inline: clamp(8px, 1.1vw, 22px) !important;
      font-size: clamp(11px, 0.95vw, 14px);
      white-space: nowrap;
    }

    /* The count bubble inside each status filter. Tagged explicitly rather
       than selected positionally, so the action buttons - whose only child is
       their own label - are not caught by it. */
    .da-count {
      padding-inline: clamp(3px, 0.5vw, 10px) !important;
      min-width: clamp(16px, 1.6vw, 26px) !important;
      font-size: clamp(13px, 1.1vw, 16px) !important;
    }

    .da-filter-row .ant-btn {
      gap: clamp(3px, 0.6vw, 8px) !important;
    }

    /* Extra room above and below the rows on a laptop - the card body padding
       is the other half of it, alongside the row gutters. */
    .da-button-card > .ant-card-body {
      padding: clamp(10px, 1.1vw, 20px) !important;
    }

    .row-WaitingLoginApproval, .row-WaitingLoginApproval td  { background: #0077b625 !important; }
    .row-OnTrip, .row-OnTrip td                { background: #f8df7841 !important; }
    .row-Logout, .row-Logout td                { background: #f5f5f5   !important; }
    .row-Available, .row-Available td             { background: #3ca70b2a !important; }
    .row-WaitingLogoutApproval, .row-WaitingLogoutApproval td { background: #023e8a25 !important; }
    .row-WaitingLeaveApproval, .row-WaitingLeaveApproval td  { background: #6a4c9325 !important; }
    .row-OnLeave, .row-OnLeave td               { background: #744d9e27 !important; }
    .row-Inactive, .row-Inactive td              { background: #fd667734 !important; }
    .row-DutyOnHold, .row-DutyOnHold td            { background: #da555225 !important; }
    .row-ResumeDuty, .row-ResumeDuty td            { background: #d68c4525 !important; }
    .row-OnPermission, .row-OnPermission td          { background: #854ad815 !important; }

    .ant-table.ant-table-bordered > .ant-table-container,
    .ant-table.ant-table-bordered > .ant-table-container > .ant-table-content > table > thead > tr > th,
    .ant-table.ant-table-bordered > .ant-table-container > .ant-table-content > table > tbody > tr > td {
      border-color: #d9d9d9 !important;
    }

    /* ---- Location modal (row pencil -> "Update Location") ---- */
    .loc-edit-btn { color: ${PRIMARY}; opacity: 0.75; }
    .loc-edit-btn:hover { opacity: 1; background: ${PRIMARY}14 !important; }

    .loc-more-btn { color: ${PRIMARY}; opacity: 0.75; }
    .loc-more-btn:hover { opacity: 1; background: ${PRIMARY}14 !important; }
    .loc-more-btn .anticon { font-size: 18px; }

    .loc-modal .ant-modal-content { padding: 0; border-radius: 16px; }
    .loc-modal .ant-modal-close { color: #fff; top: 20px; inset-inline-end: 18px; }
    .loc-modal .ant-modal-close:hover { background: rgba(255,255,255,0.18); color: #fff; }

    .loc-modal-icon {
      width: 38px; height: 38px; border-radius: 11px;
      display: inline-flex; align-items: center; justify-content: center;
      background: rgba(255,255,255,0.18);
      border: 1px solid rgba(255,255,255,0.28);
      color: #fff; flex-shrink: 0;
    }
    .loc-modal-heading {
      display: block; font-size: 19px; font-weight: 700;
      letter-spacing: 0.3px; line-height: 1.25; color: #fff;
    }
    .loc-modal-cab {
      display: inline-block; margin-top: 4px;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 11.5px; font-weight: 700; letter-spacing: 0.06em;
      color: #fff; background: rgba(255,255,255,0.18);
      border: 1px solid rgba(255,255,255,0.3);
      padding: 2px 9px; border-radius: 999px;
    }

    .loc-field-label {
      display: block; font-size: 11px; font-weight: 600;
      letter-spacing: 0.12em; text-transform: uppercase;
      color: ${TEXT_SECONDARY}; margin-bottom: 8px;
    }
    .loc-current-card {
      display: flex; align-items: flex-start; gap: 10px;
      background: ${BG_PAGE}; border: 1px solid #eee6f0;
      border-radius: 10px; padding: 12px 14px;
    }
    .loc-current-icon { flex-shrink: 0; margin-top: 2px; }
    .loc-current-text {
      font-size: 14px; font-weight: 600; color: #1f2937;
      line-height: 1.5; white-space: pre-wrap; word-break: break-word;
    }

    .loc-modal-divider.ant-divider { margin: 14px 0; border-color: #f0e9f3; }
    .loc-modal-divider .ant-divider-inner-text { padding-inline: 10px; display: inline-flex; }

    .loc-modal-actions { display: flex; justify-content: flex-end; gap: 10px; }

    @media (max-width: 575px) {
      .loc-modal .ant-modal-header { padding: 14px 18px !important; }
      .loc-modal .ant-modal-body   { padding: 18px 18px 4px !important; }
      .loc-modal .ant-modal-footer { padding: 12px 18px 16px !important; }
      .loc-modal-heading { font-size: 17px; }
      .loc-modal-actions { flex-direction: column-reverse; }
      .loc-modal-actions .ant-btn { width: 100%; }
    }
  `}
      </style>
    </div >
  );
};

export default DriverAttendance;

const styles: Record<string, CSSProperties> = {
  page: {
    padding: "5px", background: BG_PAGE, minHeight: "100%", display: "flex",
    flexDirection: "column"
  },
  headerCard: { background: "#884699", borderRadius: 16, marginBottom: 10 },
  filterCard: {
    width: "100%",
    borderRadius: 16,
    marginBottom: 24,

  },
  tableCard: { borderRadius: 16 },
};
